import { z } from 'zod';
import {
    ApiOperatorTaskBeforeDeadlineMode,
    ApiOperatorTaskOverdueMode,
    ApiOperatorTaskType,
    type OperatorTaskDetailsResponse,
    type OperatorTaskOptionsResponse,
    type OperatorTaskRelatedResponse,
    type OperatorTaskAssigneeResponse,
} from '@/core/generated/api';
import { isTaskChatRelated } from './chatContext';
import { calendarDateToDay, moscowCalendarToUtc } from '@/shared/lib/dateTime';

const clockPattern = /^([01]\d|2[0-3]):[0-5]\d$/;

const fields = z.object({
    text: z.string(),
    type: z.enum(ApiOperatorTaskType),
    dueDate: z.date()
        .nullable(),
    dueTime: z.string(),
    contextKind: z.enum(['none', 'order', 'chat']),
    related: z.custom<OperatorTaskRelatedResponse>()
        .nullable(),
    assignee: z.custom<OperatorTaskAssigneeResponse>()
        .nullable()
        .optional(),
    atDeadline: z.boolean(),
    dailyEnabled: z.boolean(),
    dailyStartDate: z.date()
        .nullable(),
    dailyTime: z.string(),
    beforeEnabled: z.boolean(),
    beforeMode: z.enum(ApiOperatorTaskBeforeDeadlineMode),
    beforeTime: z.string(),
    beforeOffset: z.number()
        .nullable(),
    overdueEnabled: z.boolean(),
    overdueMode: z.enum(ApiOperatorTaskOverdueMode),
    overdueHours: z.number()
        .nullable(),
    overdueTime: z.string(),
});

export type OperatorTaskFormValues = z.infer<typeof fields>;

/** Проверяет срок, связь и расписание задачи */
export const createOperatorTaskSchema = (
    options: OperatorTaskOptionsResponse,
    now: () => number,
    original?: OperatorTaskDetailsResponse,
) => fields.superRefine((values, ctx) => {
    const text = values.text.trim();

    if (!text) ctx.addIssue({
        code: 'custom',
        path: ['text'],
        message: 'Опишите задачу',
    });

    if (Array.from(text).length > (options.maxTextLength ?? 4000)) {
        ctx.addIssue({
            code: 'custom',
            path: ['text'],
            message: `Не более ${options.maxTextLength ?? 4000} символов`,
        });
    }

    if (!values.dueDate) ctx.addIssue({
        code: 'custom',
        path: ['dueDate'],
        message: 'Выберите дату',
    });

    if (!clockPattern.test(values.dueTime)) ctx.addIssue({
        code: 'custom',
        path: ['dueTime'],
        message: 'Введите время в формате ЧЧ:ММ',
    });

    const related = values.related;

    const validContext = values.contextKind === 'none' || related?.availability?.id === 'available' && (values.contextKind === 'order'
        ? Boolean(related.id)
        : isTaskChatRelated(related));

    const initialRelated = original?.context?.order ?? original?.context?.chat;

    const unchangedUnavailable = initialRelated?.availability?.id === 'unavailable'
        && related?.availability?.id === 'unavailable'
        && values.contextKind === (original?.context?.order
            ? 'order'
            : 'chat');

    if (!validContext && !unchangedUnavailable) {
        ctx.addIssue({
            code: 'custom',
            path: ['related'],
            message: 'Выберите связанный заказ или чат',
        });
    }

    if (values.type === ApiOperatorTaskType.OneTime && !values.atDeadline) {
        ctx.addIssue({
            code: 'custom',
            path: ['atDeadline'],
            message: 'Разовая задача требует напоминания в назначенное время',
        });
    }

    const missingDeadlineReminder = values.type === ApiOperatorTaskType.Deadline && !values.atDeadline && !values.dailyEnabled && !values.beforeEnabled;

    if (missingDeadlineReminder) {
        ctx.addIssue({
            code: 'custom',
            path: ['dailyEnabled'],
            message: 'Настройте напоминание до срока или в момент срока',
        });
    }

    const unchangedHourlyInterval = original?.reminders?.overdue?.mode?.id === ApiOperatorTaskOverdueMode.Hourly
        && original.reminders.overdue.intervalHours === values.overdueHours;

    const unsupportedHourlyInterval = values.overdueEnabled && values.overdueMode === ApiOperatorTaskOverdueMode.Hourly && !unchangedHourlyInterval
        && !(options.overdueIntervalHours ?? [1, 2, 4, 8, 12, 24]).includes(values.overdueHours ?? 0);

    if (unsupportedHourlyInterval) {
        ctx.addIssue({
            code: 'custom',
            path: ['overdueHours'],
            message: 'Выберите частоту повтора',
        });
    }

    const invalidOverdueTime = values.overdueEnabled && values.overdueMode === ApiOperatorTaskOverdueMode.Daily && !clockPattern.test(values.overdueTime);

    if (invalidOverdueTime) {
        ctx.addIssue({
            code: 'custom',
            path: ['overdueTime'],
            message: 'Введите время в формате ЧЧ:ММ',
        });
    }

    if (!values.dueDate || !clockPattern.test(values.dueTime)) return;

    const due = moscowCalendarToUtc(values.dueDate, values.dueTime);
    const dueChanged = !original || Date.parse(due) !== Date.parse(original.dueAtUtc ?? '');

    if (dueChanged && Date.parse(due) <= now()) {
        ctx.addIssue({
            code: 'custom',
            path: ['dueDate'],
            message: 'Срок должен быть в будущем',
        });
    }

    if (values.type !== ApiOperatorTaskType.Deadline) return;

    if (values.dailyEnabled) {
        if (!values.dailyStartDate) ctx.addIssue({
            code: 'custom',
            path: ['dailyStartDate'],
            message: 'Выберите начало напоминаний',
        });

        if (!clockPattern.test(values.dailyTime)) ctx.addIssue({
            code: 'custom',
            path: ['dailyTime'],
            message: 'Введите время в формате ЧЧ:ММ',
        });

        if (values.dailyStartDate) {
            const start = calendarDateToDay(values.dailyStartDate);
            const last = calendarDateToDay(values.dueDate);
            const today = new Date(now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
            const unchanged = original?.reminders?.daily?.startDate === start;

            if (start > last || ((!unchanged || dueChanged) && start < today)) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['dailyStartDate'],
                    message: 'Начало напоминаний — от сегодня до дня дедлайна',
                });
            }
        }
    }

    if (values.beforeEnabled) {
        const offset = values.beforeOffset;
        const isOffset = values.beforeMode === ApiOperatorTaskBeforeDeadlineMode.Offset;

        const invalidOffset = isOffset && (offset == null || !Number.isInteger(offset) || offset < 1 || offset > (options.maxBeforeDeadlineOffsetMinutes ?? 10080));

        if (invalidOffset) {
            ctx.addIssue({
                code: 'custom',
                path: ['beforeOffset'],
                message: `Укажите от 1 до ${options.maxBeforeDeadlineOffsetMinutes ?? 10080} минут`,
            });

            return;
        }

        if (!isOffset && !clockPattern.test(values.beforeTime)) {
            ctx.addIssue({
                code: 'custom',
                path: ['beforeTime'],
                message: 'Введите время в формате ЧЧ:ММ',
            });

            return;
        }

        const moment = isOffset
            ? Date.parse(due) - (offset ?? 0) * 60000
            : Date.parse(moscowCalendarToUtc(values.dueDate, values.beforeTime));

        const previous = original?.reminders?.beforeDeadline;

        const unchanged = previous?.mode?.id === values.beforeMode && (isOffset
            ? previous.offsetMinutes === offset
            : previous?.time === values.beforeTime);

        if (moment >= Date.parse(due) || ((!unchanged || dueChanged) && moment <= now())) {
            ctx.addIssue({
                code: 'custom',
                path: [isOffset
                    ? 'beforeOffset'
                    : 'beforeTime'],
                message: 'Напоминание должно быть в будущем и раньше дедлайна',
            });
        }
    }
});
